/**
 * VendaFormDialog Component - Formulário de Venda
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { VendaFormData, vendaSchema } from "@/schemas/vendaSchema";
import { Venda, VendaItem, FormaPagamento, StatusPagamento, CanalVenda, SituacaoEntrega } from "@/types/venda";
import { ClienteCombobox } from "@/components/cliente/ClienteCombobox";
import { EstabelecimentoCombobox } from "@/components/estabelecimento/EstabelecimentoCombobox";
import { VendaItensManager } from "./VendaItensManager";

interface VendaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  venda: Venda | null;
  onSubmit: (data: VendaFormData) => void;
  isLoading: boolean;
}

const formasPagamento: FormaPagamento[] = [
  "Dinheiro",
  "Cartão de Crédito",
  "Cartão de Débito",
  "Pix",
  "Fiado",
  "Promocional",
];

const statusPagamento: StatusPagamento[] = ["Pago", "Pendente", "Cancelado"];
const canaisVenda: CanalVenda[] = ["aplicativo", "presencial", "delivery"];
const situacoesEntrega: SituacaoEntrega[] = ["em preparo", "entregue", "aguardando retirada"];

export function VendaFormDialog({
  open,
  onOpenChange,
  mode,
  venda,
  onSubmit,
  isLoading,
}: VendaFormDialogProps) {
  const [itens, setItens] = useState<VendaItem[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<VendaFormData>({
    resolver: zodResolver(vendaSchema),
    defaultValues: {
      data_venda: new Date().toISOString().split("T")[0],
      forma_pagamento: "Dinheiro",
      status_pagamento: "Pendente",
      subtotal: 0,
      desconto_total: 0,
      valor_frete: 0,
      valor_total: 0,
      itens: [],
    },
  });

  useEffect(() => {
    if (open) {
      if (mode === "edit" && venda) {
        // Garantir que itens sempre seja um array
        const vendaItens = Array.isArray(venda.itens) ? venda.itens : [];
        
        reset({
          data_venda: venda.data_venda,
          cliente_id: venda.cliente_id || undefined,
          estabelecimento_id: venda.estabelecimento_id || undefined,
          forma_pagamento: venda.forma_pagamento,
          status_pagamento: venda.status_pagamento,
          observacoes: venda.observacoes || undefined,
          responsavel_venda: venda.responsavel_venda || undefined,
          canal_venda: venda.canal_venda || undefined,
          observacoes_gerais: venda.observacoes_gerais || undefined,
          numero_pedido: venda.numero_pedido || undefined,
          codigo_fiscal_cupom: venda.codigo_fiscal_cupom || undefined,
          data_entrega: venda.data_entrega || undefined,
          situacao_entrega: venda.situacao_entrega || undefined,
          subtotal: venda.subtotal,
          desconto_total: venda.desconto_total,
          valor_frete: venda.valor_frete,
          valor_total: venda.valor_total,
          itens: vendaItens,
        });
        setItens(vendaItens);
      } else {
        reset({
          data_venda: new Date().toISOString().split("T")[0],
          forma_pagamento: "Dinheiro",
          status_pagamento: "Pendente",
          subtotal: 0,
          desconto_total: 0,
          valor_frete: 0,
          valor_total: 0,
          itens: [],
        });
        setItens([]);
      }
    }
  }, [open, mode, venda, reset]);

  // Calcular totais
  useEffect(() => {
    const subtotal = itens.reduce((acc, item) => acc + item.subtotal, 0);
    const desconto = watch("desconto_total") || 0;
    const frete = watch("valor_frete") || 0;
    const total = subtotal - desconto + frete;

    setValue("subtotal", subtotal);
    setValue("valor_total", total);
    setValue("itens", itens);
  }, [itens, watch("desconto_total"), watch("valor_frete"), setValue]);

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit(data);
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Nova Venda" : "Editar Venda"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit} className="space-y-6">
          {/* Seção: Dados da Venda */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">📋 Dados da Venda</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="data_venda">Data da Venda *</Label>
                <Input
                  id="data_venda"
                  type="date"
                  {...register("data_venda")}
                />
                {errors.data_venda && (
                  <span className="text-sm text-destructive">
                    {errors.data_venda.message}
                  </span>
                )}
              </div>

              <div>
                <Label>Cliente</Label>
                <ClienteCombobox
                  value={watch("cliente_id") || ""}
                  onValueChange={(value) => setValue("cliente_id", value)}
                />
              </div>

              <div>
                <Label>Estabelecimento</Label>
                <EstabelecimentoCombobox
                  value={watch("estabelecimento_id") || ""}
                  onValueChange={(value) => setValue("estabelecimento_id", value)}
                />
              </div>

              <div>
                <Label htmlFor="forma_pagamento">Forma de Pagamento *</Label>
                <Select
                  value={watch("forma_pagamento")}
                  onValueChange={(value) =>
                    setValue("forma_pagamento", value as FormaPagamento)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {formasPagamento.map((forma) => (
                      <SelectItem key={forma} value={forma}>
                        {forma}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.forma_pagamento && (
                  <span className="text-sm text-destructive">
                    {errors.forma_pagamento.message}
                  </span>
                )}
              </div>

              <div>
                <Label htmlFor="status_pagamento">Status do Pagamento *</Label>
                <Select
                  value={watch("status_pagamento")}
                  onValueChange={(value) =>
                    setValue("status_pagamento", value as StatusPagamento)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {statusPagamento.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.status_pagamento && (
                  <span className="text-sm text-destructive">
                    {errors.status_pagamento.message}
                  </span>
                )}
              </div>

              <div>
                <Label htmlFor="canal_venda">Canal de Venda</Label>
                <Select
                  value={watch("canal_venda") || ""}
                  onValueChange={(value) =>
                    setValue("canal_venda", value as CanalVenda)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {canaisVenda.map((canal) => (
                      <SelectItem key={canal} value={canal}>
                        {canal}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="numero_pedido">Número do Pedido</Label>
                <Input id="numero_pedido" {...register("numero_pedido")} />
              </div>

              <div>
                <Label htmlFor="codigo_fiscal_cupom">Código Fiscal/Cupom</Label>
                <Input
                  id="codigo_fiscal_cupom"
                  {...register("codigo_fiscal_cupom")}
                />
              </div>

              <div>
                <Label htmlFor="responsavel_venda">Responsável pela Venda</Label>
                <Input
                  id="responsavel_venda"
                  {...register("responsavel_venda")}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                {...register("observacoes")}
                rows={2}
              />
            </div>
          </div>

          <Separator />

          {/* Seção: Itens da Venda */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">🛒 Itens da Venda</h3>
            <VendaItensManager
              itens={itens}
              onItensChange={(newItens) => {
                setItens(newItens);
                setValue("itens", newItens);
              }}
            />
            {errors.itens && (
              <span className="text-sm text-destructive">
                {errors.itens.message}
              </span>
            )}
          </div>

          <Separator />

          {/* Seção: Totais e Ajustes */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">💰 Totais e Ajustes</h3>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resumo Financeiro</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Subtotal</Label>
                    <div className="text-xl font-semibold">
                      {formatCurrency(watch("subtotal"))}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="desconto_total">Desconto Total (R$)</Label>
                    <Input
                      id="desconto_total"
                      type="number"
                      step="0.01"
                      {...register("desconto_total", { valueAsNumber: true })}
                    />
                    {errors.desconto_total && (
                      <span className="text-sm text-destructive">
                        {errors.desconto_total.message}
                      </span>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="valor_frete">Valor do Frete (R$)</Label>
                    <Input
                      id="valor_frete"
                      type="number"
                      step="0.01"
                      {...register("valor_frete", { valueAsNumber: true })}
                    />
                  </div>
                </div>

                <Separator />

                <div className="text-center">
                  <Label className="text-muted-foreground">Valor Total</Label>
                  <div className="text-3xl font-bold text-primary">
                    {formatCurrency(watch("valor_total"))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Separator />

          {/* Seção: Entrega */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">🚚 Entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_entrega">Data de Entrega</Label>
                <Input
                  id="data_entrega"
                  type="date"
                  {...register("data_entrega")}
                />
              </div>

              <div>
                <Label htmlFor="situacao_entrega">Situação da Entrega</Label>
                <Select
                  value={watch("situacao_entrega") || ""}
                  onValueChange={(value) =>
                    setValue("situacao_entrega", value as SituacaoEntrega)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {situacoesEntrega.map((situacao) => (
                      <SelectItem key={situacao} value={situacao}>
                        {situacao}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="observacoes_gerais">Observações Gerais da Entrega</Label>
              <Textarea
                id="observacoes_gerais"
                {...register("observacoes_gerais")}
                rows={3}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4 border-t mt-4">
            {itens.length === 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md">
                <span className="text-amber-600 dark:text-amber-400 text-sm">
                  ⚠️ Adicione pelo menos um item para salvar a venda
                </span>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isLoading || itens.length === 0}
                title={itens.length === 0 ? "Adicione pelo menos um item para salvar" : ""}
              >
                {isLoading ? "Salvando..." : mode === "create" ? "Criar Venda" : "Salvar Alterações"}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
